import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { TestResourceApi } from "../../api/TestResourceApi";
import type { CreateTestChildResource } from "../../generated/dto/CreateTestChildResource";
import type { CreateTestResource } from "../../generated/dto/CreateTestResource";
import type { DeleteTestChildResource } from "../../generated/dto/DeleteTestChildResource";
import type { DeleteTestResource } from "../../generated/dto/DeleteTestResource";
import type { TestChildResource } from "../../generated/dto/TestChildResource";
import type { TestChildResourceFetchOptions } from "../../generated/dto/TestChildResourceFetchOptions";
import type { TestResource } from "../../generated/dto/TestResource";
import type { TestResourceDescribeChildRequest } from "../../generated/dto/TestResourceDescribeChildRequest";
import type { TestResourceFetchOptions } from "../../generated/dto/TestResourceFetchOptions";
import type { TestResourceFormatChildLabelsRequest } from "../../generated/dto/TestResourceFormatChildLabelsRequest";
import type { TestResourceFormatTextWithFlagRequest } from "../../generated/dto/TestResourceFormatTextWithFlagRequest";
import type { TestResourceLoadBoundResourceChildrenRequest } from "../../generated/dto/TestResourceLoadBoundResourceChildrenRequest";
import type { TestResourceRef } from "../../generated/dto/TestResourceRef";
import type { TestResourceReturnBoundResourceEnvelopeRequest } from "../../generated/dto/TestResourceReturnBoundResourceEnvelopeRequest";
import type { TestResourceReturnSampleTimestampRequest } from "../../generated/dto/TestResourceReturnSampleTimestampRequest";
import type { TestResourceSummary } from "../../generated/dto/TestResourceSummary";
import type { TestResourceTransportEnvelope } from "../../generated/dto/TestResourceTransportEnvelope";
import type { UpdateTestChildResource } from "../../generated/dto/UpdateTestChildResource";
import type { UpdateTestResource } from "../../generated/dto/UpdateTestResource";

export class TestResourceApiClient implements TestResourceApi {
  constructor(private readonly client = useClient()) {}

  async getTestResource(id: number, options?: TestResourceFetchOptions): Promise<TestResource> {
    const { data } = await this.client.get<unknown>(`/testresource/internal/${id}`, { params: serializeCbiDto(options, "generated.dto.TestResourceFetchOptions") });
    return deserializeCbiDto<TestResource>(data, "generated.dto.TestResource");
  }

  async getTestChildResource(id: number, options?: TestChildResourceFetchOptions): Promise<TestChildResource> {
    const { data } = await this.client.get<unknown>(`/testchildresource/internal/${id}`, { params: serializeCbiDto(options, "generated.dto.TestChildResourceFetchOptions") });
    return deserializeCbiDto<TestChildResource>(data, "generated.dto.TestChildResource");
  }

  async createTestResource(source: CreateTestResource): Promise<TestResource> {
    const { data } = await this.client.post<unknown>(`/testresource/internal`, serializeCbiDto(source, "generated.dto.CreateTestResource"));
    return deserializeCbiDto<TestResource>(data, "generated.dto.TestResource");
  }

  async createTestChildResource(source: CreateTestChildResource): Promise<TestChildResource> {
    const { data } = await this.client.post<unknown>(`/testchildresource/internal`, serializeCbiDto(source, "generated.dto.CreateTestChildResource"));
    return deserializeCbiDto<TestChildResource>(data, "generated.dto.TestChildResource");
  }

  async updateTestResource(source: UpdateTestResource): Promise<TestResource> {
    const { data } = await this.client.put<unknown>(`/testresource/internal/${source.id}`, serializeCbiDto(source, "generated.dto.UpdateTestResource"));
    return deserializeCbiDto<TestResource>(data, "generated.dto.TestResource");
  }

  async updateTestChildResource(source: UpdateTestChildResource): Promise<TestChildResource> {
    const { data } = await this.client.put<unknown>(`/testchildresource/internal/${source.id}`, serializeCbiDto(source, "generated.dto.UpdateTestChildResource"));
    return deserializeCbiDto<TestChildResource>(data, "generated.dto.TestChildResource");
  }

  async deleteTestResource(source: DeleteTestResource): Promise<void> {
    await this.client.delete(`/testresource/internal/${source.id}`);
  }

  async deleteTestChildResource(source: DeleteTestChildResource): Promise<void> {
    await this.client.delete(`/testchildresource/internal/${source.id}`);
  }

  async getTestResourceTestChildren(id: number, options?: TestChildResourceFetchOptions): Promise<Array<TestChildResource>> {
    const { data } = await this.client.get<unknown>(`/testresource/internal/${id}/testchildren`, { params: serializeCbiDto(options, "generated.dto.TestChildResourceFetchOptions") });
    return deserializeCbiDto<Array<TestChildResource>>(data, "generated.dto.TestChildResource");
  }

  async getTestChildResourceTestResource(id: number, options?: TestResourceFetchOptions): Promise<TestResource> {
    const { data } = await this.client.get<unknown>(`/testchildresource/internal/${id}/testresource`, { params: serializeCbiDto(options, "generated.dto.TestResourceFetchOptions") });
    return deserializeCbiDto<TestResource>(data, "generated.dto.TestResource");
  }

  async acceptTextValueStaff(value: string): Promise<void> {
    await this.client.post(`/testresource/staff/accepttextvalue`, JSON.stringify(value));
  }

  async computedValueTestStaff(ref: TestResourceRef): Promise<string> {
    const { data } = await this.client.post<unknown>(`/testresource/staff/computedvaluetest`, serializeCbiDto(ref, "generated.dto.TestResourceRef"));
    return data as string;
  }

  async describeChildStaff(request: TestResourceDescribeChildRequest): Promise<string> {
    const { data } = await this.client.post<unknown>(`/testresource/staff/describechild`, serializeCbiDto(request, "generated.dto.TestResourceDescribeChildRequest"));
    return data as string;
  }

  async formatChildLabelsStaff(request: TestResourceFormatChildLabelsRequest): Promise<string> {
    const { data } = await this.client.post<unknown>(`/testresource/staff/formatchildlabels`, serializeCbiDto(request, "generated.dto.TestResourceFormatChildLabelsRequest"));
    return data as string;
  }

  async formatTextWithFlagStaff(request: TestResourceFormatTextWithFlagRequest): Promise<string> {
    const { data } = await this.client.post<unknown>(`/testresource/staff/formattextwithflag`, serializeCbiDto(request, "generated.dto.TestResourceFormatTextWithFlagRequest"));
    return data as string;
  }

  async getTestResourceSummaryStaff(): Promise<TestResourceSummary> {
    const { data } = await this.client.post<unknown>(`/testresource/staff/gettestresourcesummary`);
    return deserializeCbiDto<TestResourceSummary>(data, "generated.dto.TestResourceSummary");
  }

  async loadBoundResourceChildrenStaff(request: TestResourceLoadBoundResourceChildrenRequest): Promise<TestResourceTransportEnvelope> {
    const { data } = await this.client.post<unknown>(`/testresource/staff/loadboundresourcechildren`, serializeCbiDto(request, "generated.dto.TestResourceLoadBoundResourceChildrenRequest"));
    return deserializeCbiDto<TestResourceTransportEnvelope>(data, "generated.dto.TestResourceTransportEnvelope");
  }

  async loadChildrenFromReferenceStaff(ref: TestResourceRef): Promise<void> {
    await this.client.post(`/testresource/staff/loadchildrenfromreference`, serializeCbiDto(ref, "generated.dto.TestResourceRef"));
  }

  async loadTestResourceChildrenStaff(): Promise<void> {
    await this.client.post(`/testresource/staff/loadtestresourcechildren`);
  }

  async returnBoundResourceEnvelopeStaff(request: TestResourceReturnBoundResourceEnvelopeRequest): Promise<TestResourceTransportEnvelope> {
    const { data } = await this.client.post<unknown>(`/testresource/staff/returnboundresourceenvelope`, serializeCbiDto(request, "generated.dto.TestResourceReturnBoundResourceEnvelopeRequest"));
    return deserializeCbiDto<TestResourceTransportEnvelope>(data, "generated.dto.TestResourceTransportEnvelope");
  }

  async returnSampleTimestampStaff(request: TestResourceReturnSampleTimestampRequest): Promise<Date> {
    const { data } = await this.client.post<unknown>(`/testresource/staff/returnsampletimestamp`, serializeCbiDto(request, "generated.dto.TestResourceReturnSampleTimestampRequest"));
    return data as Date;
  }

  async verifyBoundResourceStaff(dto: TestResourceRef): Promise<void> {
    await this.client.post(`/testresource/staff/verifyboundresource`, serializeCbiDto(dto, "generated.dto.TestResourceRef"));
  }
}

export const useTestResourceApi = (): TestResourceApi => new TestResourceApiClient();
